import { t } from "@i18n";
import { MdOutlineWrapText } from "react-icons/md";
import { BUTTON_TYPES } from "../../../cubeComponent/constants/globalVariable";
import ButtonComponent from "../../../cubeComponent/form/ButtonComponent";
import IconComponent from "../../../cubeComponent/icons";
import { manageWBSItemSteps, mileStoneStatus } from "./constant";
import { folderPermissions } from "../../../../../helpers/index";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";

/** Just the two members this permission check reads off the loaded Contract (contracts.schema.ts). */
type ContractCRUDDetails = {
  userPermissions?: number[];
  lockData?: { isLocked?: boolean };
};

/** The change-order toggle every contracts screen keeps in local state. */
type ContractCRUDChangeOrder = { isEnabled?: boolean };

/**
 * Both parameters stay `unknown`: a dozen contracts screens hand down `contractDetails` and
 * `changeOrderState` values of several different shapes (one passes `ChangeOrderState | null`), and
 * narrowing this exported surface breaks them. The two members actually read are asserted per read.
 */
export function getContractCRUDPermission(contractDetails: unknown = {}, changeOrderState: unknown = {}): boolean {
  const { RESOURCE_MODIFY } = folderPermissions();
  if (!(contractDetails as ContractCRUDDetails).userPermissions?.includes(RESOURCE_MODIFY.id)) {
    return false;
  }
  // KNOWN BUG, preserved verbatim: when the Contract is locked this returns `isEnabled`, which may be
  // `undefined` rather than `false`. Every caller only tests truthiness; erased `as boolean`.
  return (!(contractDetails as ContractCRUDDetails).lockData?.isLocked ||
    (changeOrderState as ContractCRUDChangeOrder)?.isEnabled) as boolean;
}

/**
 * A summable tree row. Callers pass rows of several different shapes (BOQ, WBS, milestone), so the
 * element stays `unknown` and each read is asserted to this view.
 */
type TotalRow = { children?: unknown[] | null } & Record<string, unknown>;

export function getTotal(records: readonly unknown[] | null | undefined, fieldName: string): number {
  if (!records) {
    return 0;
  }
  return records?.reduce((total: number, object: unknown) => {
    if ((object as TotalRow).children) {
      return total + getTotal((object as TotalRow).children, fieldName);
    }
    // KNOWN BUG, preserved verbatim: a row missing `fieldName` makes the running total NaN.
    return total + ((object as TotalRow)[fieldName] as number);
  }, 0);
}

export function changeTypeButtonTemplate() {
  return (
    <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="changeType">
      <span className="flex items-center gap-1">
        <IconComponent name="changeType" width="1rem" height="1rem" />
        {t("Change Type")}
      </span>
    </ButtonComponent>
  );
}

export function editButtonTemplate() {
  return (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="edit">
        <span className="flex items-center gap-1">
          <IconComponent name="editProfile" width="1.25rem" height="1.25rem" />
          {t("Edit")}
        </span>
      </ButtonComponent>
    </div>
  );
}
export function updateButtonTemplate() {
  return (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="update">
        <span className="flex items-center gap-1">
          <IconComponent name="Update" width="1rem" height="1rem" />
          {t("Update")}
        </span>
      </ButtonComponent>
    </div>
  );
}

export function cancelButtonTemplate() {
  return (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="cancel">
        <span className="flex items-center gap-1">
          <IconComponent name="Cancel" width="1rem" height="1rem" />
          {t("Cancel")}
        </span>
      </ButtonComponent>
    </div>
  );
}

export function deleteBtnTemplate() {
  return (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="delete">
        <span className="flex items-center gap-1">
          <IconComponent name="delete" width="1.25rem" height="1.25rem" />
          {t("Delete")}
        </span>
      </ButtonComponent>
    </div>
  );
}
export function assignWBSTemplate() {
  return (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="attachToWBSItem">
        <span className="flex items-center gap-1">
          <IconComponent name="smallAttachmentIcon" width="1rem" height="1rem" />
          {t("Assign To WBS")}
        </span>
      </ButtonComponent>
    </div>
  );
}
export function wrapTextButtonTemplate(state = false) {
  return (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="wrap_text">
        <span className="flex items-center gap-1">
          <MdOutlineWrapText />
          {state ? t("Un Wrap Text") : t("Wrap Text")}
        </span>
      </ButtonComponent>
    </div>
  );
}

/** Row fields the quantity / amount cell templates address. Mirrors WBSItemDetails' own unions. */
type BoqQuantityField =
  | "quantity"
  | "revisedQuantity"
  | "changeOrderQuantity"
  | "usedQuantity"
  | "remainingQuantity"
  | "inspectedQuantity"
  | "billedQuantity"
  | "availableQuantity";

type BoqAmountField =
  | "amount"
  | "revisedAmount"
  | "changeOrderAmount"
  | "usedAmount"
  | "remainingAmount"
  | "inspectedAmount"
  | "billedAmount";

/**
 * The BOQ row type belongs to the caller, not to this shared helper — it builds columns for the
 * contracts WBS grid, the forms BOQ grid and the estimation grid, whose rows differ, and
 * `ReturnType<typeof getGridColumns>` is read by WBSItemDetails, so a type parameter would
 * instantiate to `unknown` there and break it. Row slots are therefore `never`: the widest
 * contravariant position, so any concrete row-typed template is accepted. Same device as
 * `features/files`' `handleFileSelectedButtonClick(selected: never, …)`.
 */
type BoqRow = never;

/** A SlickGrid cell formatter as this file builds them: (row, cell, value, columnDef, dataContext). */
type BoqCellFormatter = (data: number, value: number, a: unknown, b: unknown, args: BoqRow) => unknown;

/** A slot this helper only forwards into a column definition and never calls itself. */
type ForwardedTemplate = (...args: never[]) => unknown;

type GetGridColumnsArgs = {
  source: string;
  showManageItemModal: { step?: string | null };
  changeOrderState: { isEnabled?: boolean };
  /** slickgrid-react's `Formatters`; only `tree` is used. */
  Formatters: { tree: unknown };
  itemTypeTemplate: BoqCellFormatter;
  unitTemplate: BoqCellFormatter;
  detailTemplate: BoqCellFormatter;
  wbsItemsTemplate: BoqCellFormatter;
  quantityTemplate: (args: BoqRow, field: BoqQuantityField) => unknown;
  amountTemplate: (args: BoqRow, field: BoqAmountField) => unknown;
  linkedQuantityTemplate: (args: BoqRow) => unknown;
  /**
   * KNOWN BUG, preserved verbatim: `assignedQuantityTemplate` and `remarksTemplate` take a row but
   * are installed directly as `formatter`, so SlickGrid calls them with the row INDEX, not the row.
   */
  assignedQuantityTemplate: (args: BoqRow) => unknown;
  remarksTemplate: (args: BoqRow) => unknown;
  editTemplate: ForwardedTemplate;
  HSN_SAC_EditTemplate: ForwardedTemplate;
  descEditTemplate: ForwardedTemplate;
  rateEditTemplate: ForwardedTemplate;
  unitEditTemplate: ForwardedTemplate;
  quantityEditTemplate: ForwardedTemplate;
  showInfoColumn?: boolean;
  /** Read only for truthiness; WBSItemDetails passes an `||` of two optional booleans. */
  showDetailedColumns?: unknown;
  showQuantityInputBox?: boolean;
  showRemarksColumn?: boolean;
  isRustomjeePortal?: boolean;
  /** Passed by WBSItemDetails but not consumed here; declared so the call keeps type-checking. */
  allowCheckBoxSelection?: boolean;
};

export const getGridColumns = ({
  source,
  showManageItemModal,
  editTemplate,
  itemTypeTemplate,
  HSN_SAC_EditTemplate,
  descEditTemplate,
  rateEditTemplate,
  unitEditTemplate,
  unitTemplate,
  quantityEditTemplate,
  quantityTemplate,
  linkedQuantityTemplate,
  amountTemplate,
  assignedQuantityTemplate,
  detailTemplate,
  showInfoColumn = false,
  showDetailedColumns = false,
  wbsItemsTemplate,
  showQuantityInputBox,
  showRemarksColumn = false,
  remarksTemplate,
  isRustomjeePortal = false,
  changeOrderState,
  Formatters,
}: GetGridColumnsArgs) => {
  const columnsList = [
    ...(showInfoColumn
      ? [
          {
            field: "sourceFormNo",
            id: "sourceFormNo",
            name: t("Info"),
            formatter: detailTemplate,
            width: 100,
          },
        ]
      : []),
    {
      field: "serial",
      id: "serial",
      name: t("S. No"),
      width: 120,
      formatter: Formatters.tree,
      minWidth: 100,
    },
    {
      name: t("Item Type"),
      field: "type",
      id: "type",
      minWidth: 100,
      filterType: "Text",
      formatter: itemTypeTemplate,
    },
    {
      name: t("Code"),
      field: "code",
      id: "code",
      width: 120,
      minWidth: 100,
      filterType: "Text",
      allowEditing: false,
      // The only formatter here that reads a row field itself, so it names the one member it needs.
      formatter: (data: number, value: number, a: unknown, b: unknown, args: { code?: string }) => args.code ?? "",
    },
    {
      name: t("HSN/SAC"),
      field: "HSN_SAC",
      id: "HSN_SAC",
      width: 120,
      minWidth: 100,
      filterType: "Text",
      editorTemplate: HSN_SAC_EditTemplate,
    },
    {
      name: t("Description"),
      field: "description",
      id: "description",
      width: 120,
      minWidth: 100,
      filterType: "Text",
      editorTemplate: descEditTemplate,
    },
    ...(showRemarksColumn && source == "FormView"
      ? [
          {
            field: "remarks",
            id: "remarks",
            name: t("Remarks"),
            width: 150,
            filterType: "Text",
            formatter: remarksTemplate,
          },
        ]
      : []),
    {
      name: t("Unit"),
      field: "purchaseUnit",
      id: "purchaseUnit",
      _valueGetter: (args: { purchaseUnit?: { name?: string } }) => args?.purchaseUnit?.name,
      minWidth: 150,
      formatter: unitTemplate,
      filterType: "Text",
      editorTemplate: unitEditTemplate,
    },
    {
      name: t("Rate"),
      field: "rate",
      id: "rate",
      width: 120,
      minWidth: 100,
      filterType: "Number",
      editorTemplate: rateEditTemplate,
    },
    ...(source == "WBSView"
      ? [
          {
            name: t("Assigned Quantity"),
            field: "availableQuantity",
            id: "availableQuantity",
            minWidth: 100,
            width: 120,
            formatter: assignedQuantityTemplate,
          },
        ]
      : []),
    ...(source == "BoqView"
      ? [
          {
            name: t("WBS Items"),
            field: "wbsItems",
            id: "wbsItems",
            minWidth: 100,
            formatter: wbsItemsTemplate,
            width: 120,
          },
        ]
      : []),
    ...(source == "FormView" || !showDetailedColumns
      ? [
          {
            name: t("Quantity"),
            field: "quantity",
            id: "quantity",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              quantityTemplate(
                args,
                showManageItemModal.step === manageWBSItemSteps.showParentWBSItems ? "revisedQuantity" : "quantity",
              ),
            editorTemplate: quantityEditTemplate,
          },
          {
            name: t("Amount"),
            field: "revisedAmount",
            id: "revisedAmount",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              amountTemplate(
                args,
                showManageItemModal.step === manageWBSItemSteps.showParentWBSItems ? "revisedAmount" : "amount",
              ),
          },
        ]
      : [
          {
            name: t("Quantity"),
            field: "quantity",
            id: "quantity",
            columnGroup: "Quantity",
            allowEditing: !changeOrderState.isEnabled,
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              quantityTemplate(args, "quantity"),
            editorTemplate: quantityEditTemplate,
          },
          {
            name: t("Amount"),
            field: "amount",
            id: "amount",
            columnGroup: "Quantity",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              amountTemplate(args, "amount"),
            editorTemplate: editTemplate,
          },
          {
            name: t("Quantity"),
            field: "changeOrderQuantity",
            id: "changeOrderQuantity",
            width: 120,
            minWidth: 100,
            columnGroup: "Change Order",
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              quantityTemplate(args, "changeOrderQuantity"),
          },
          {
            name: t("Amount"),
            field: "changeOrderAmount",
            id: "changeOrderAmount",
            width: 120,
            minWidth: 100,
            columnGroup: "Change Order",
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              amountTemplate(args, "changeOrderAmount"),
          },
          {
            name: t("Quantity"),
            field: "revisedQuantity",
            id: "revisedQuantity",
            width: 120,
            minWidth: 100,
            columnGroup: "Revised",
            editorTemplate: quantityEditTemplate,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              quantityTemplate(args, "revisedQuantity"),
          },
          {
            name: t("Amount"),
            field: "revisedAmount",
            id: "revisedAmount",
            width: 120,
            minWidth: 100,
            columnGroup: "Revised",
            allowEditing: false,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              amountTemplate(args, "revisedAmount"),
          },
          ...(isRustomjeePortal
            ? []
            : [
                {
                  name: t("Quantity"),
                  field: "usedQuantity",
                  id: "usedQuantity",
                  columnGroup: "Used",
                  width: 120,
                  minWidth: 100,
                  formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
                    quantityTemplate(args, "usedQuantity"),
                  allowEditing: false,
                },
                {
                  name: t("Amount"),
                  field: "usedAmount",
                  id: "usedAmount",
                  columnGroup: "Used",
                  width: 120,
                  minWidth: 100,
                  formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
                    amountTemplate(args, "usedAmount"),
                  allowEditing: false,
                },
              ]),
          ...(isRustomjeePortal
            ? []
            : [
                {
                  name: t("Quantity"),
                  field: "remainingQuantity",
                  id: "remainingQuantity",
                  width: 120,
                  minWidth: 100,
                  columnGroup: "Remaining",
                  formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
                    quantityTemplate(args, "remainingQuantity"),
                },
                {
                  name: t("Amount"),
                  columnGroup: "Remaining",
                  field: "remainingAmount",
                  id: "remainingAmount",
                  width: 120,
                  minWidth: 100,
                  formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
                    amountTemplate(args, "remainingAmount"),
                },
              ]),
          {
            name: t("Quantity"),
            columnGroup: `${isRustomjeePortal ? t("Work Done") : t("Inspected")}`,
            field: "inspectedQuantity",
            id: "inspectedQuantity",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              quantityTemplate(args, "inspectedQuantity"),
          },
          {
            name: t("Amount"),
            columnGroup: `${isRustomjeePortal ? "Work Done" : "Inspected"}`,
            field: "inspectedAmount",
            id: "inspectedAmount",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              amountTemplate(args, "inspectedAmount"),
          },
          {
            name: t("Quantity"),
            field: "billedQuantity",
            columnGroup: "Billed",
            id: "billedQuantity",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              quantityTemplate(args, "billedQuantity"),
          },
          {
            name: t("Amount"),
            columnGroup: "Billed",
            field: "billedAmount",
            id: "billedAmount",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              amountTemplate(args, "billedAmount"),
          },
        ]),
    ...(showManageItemModal.step === manageWBSItemSteps.showParentWBSItems || source == "FormView"
      ? [
          {
            name: t("Available Quantity"),
            field: "availableQuantity",
            id: "availableQuantity",
            width: 120,
            minWidth: 100,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              quantityTemplate(args, "availableQuantity"),
          },
        ]
      : []),
    ...(showManageItemModal.step == manageWBSItemSteps.showParentWBSItems || showQuantityInputBox
      ? [
          {
            name: t("Linked Quantity"),
            field: "availableQuantity",
            id: "availableQuantity",
            minWidth: 100,
            width: 120,
            formatter: (data: number, value: number, a: unknown, b: unknown, args: BoqRow) =>
              linkedQuantityTemplate(args),
          },
        ]
      : []),
  ];
  return columnsList;
};
export const noAccessSection = () => (
  <div className="w-full h-full flex gap-4 flex-col items-center justify-center overflow-auto">
    <IconComponent name="NoAccess" width="8rem" height="8rem" />
    <span className="text-xl font-semibold">
      {`You don't have access to this section because you dont have access to respective contract`}
    </span>
  </div>
);
export const mileStonStatusTemplate = (status: string | undefined, isSlickGrid = false) => {
  let color = "bg-system_colour_45";
  if (status == mileStoneStatus.yetToStart) {
    color = "bg-system_colour_45";
  } else if (status == mileStoneStatus.inProgress) {
    color = "bg-system_colour_12";
  } else if (status == mileStoneStatus.achieved) {
    color = "bg-positive_communication";
  } else if (status == mileStoneStatus.delayed) {
    color = "bg-system_colour_8";
  } else if (status == mileStoneStatus.beforeTimeCompletion) {
    color = "bg-positive_communication";
  } else if (status == mileStoneStatus.achievedButDelayed) {
    color = "bg-system_colour_8";
  }
  if (isSlickGrid) {
    const container = document.createElement("div");
    const root = createRoot(container);
    root.render(
      <div className={`h-8 ${color} w-fit ps-3 gap-2 bg-opacity-25 rounded-2xl pe-3 flex items-center`}>
        <span className={`${color} rounded-full h-3 w-3`} />
        <span className="font-semibold">{status}</span>
      </div>,
    );
    return container;
  }

  return (
    <div className={`h-8 ${color} w-fit ps-3 gap-2 bg-opacity-25 rounded-2xl pe-3 flex items-center`}>
      <span className={`${color} rounded-full h-3 w-3`} />
      <span className="font-semibold">{status}</span>
    </div>
  );
};
export const DisabledIllustration = () => {
  return (
    <div className="flex justify-center items-center flex-col gap-4 w-full h-full">
      <IconComponent name="disabledModule" width="19.3125rem" height="14.875rem" />
      <span className="font-semibold text-2xl text-text_gray">{t("This Module is Disabled")}</span>
      <span>{t("To Enable it, Please visit to the Detail Section")}</span>
    </div>
  );
};
